import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FileCheck, CheckCircle, Circle, XCircle, Ban } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { selfAPI } from '../services/api';
import { formatCurrency } from '../utils/formatters';

const REJECTION_LABELS = {
  invalid_documents: 'Invalid Documents',
  incorrect_information: 'Incorrect Information',
  relationship_proof_missing: 'Relationship Proof Missing',
  other: 'Other',
};

const STATUS_STEPS = [
  { key: 'submitted', label: 'Submitted' },
  { key: 'pending', label: 'Under Review' },
  { key: 'decision', label: 'Decision' },
];

function stepState(request, stepKey) {
  const status = request.status;
  if (stepKey === 'submitted') {
    return 'complete';
  }
  if (stepKey === 'pending') {
    if (status === 'pending') return 'current';
    if (['approved', 'rejected', 'cancelled', 'superseded'].includes(status)) return 'complete';
    return 'upcoming';
  }
  if (stepKey === 'decision') {
    if (status === 'pending') return 'upcoming';
    return status === 'approved' ? 'complete' : 'failed';
  }
  return 'upcoming';
}

function StepIcon({ state }) {
  if (state === 'complete') return <CheckCircle className="w-5 h-5 text-green-600" />;
  if (state === 'failed') return <XCircle className="w-5 h-5 text-red-600" />;
  if (state === 'current') return <Circle className="w-5 h-5 text-amber-500 fill-amber-100" />;
  return <Circle className="w-5 h-5 text-slate-300" />;
}

export default function EmployeeFamilyRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  const load = () => selfAPI.getFamilyRequest(id)
    .then(({ data }) => setRequest(data))
    .catch(() => setRequest(null));

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [id]);

  const handleCancel = async () => {
    if (!window.confirm('Cancel this pending request?')) return;
    setCancelling(true);
    setCancelError(null);
    try {
      const { data } = await selfAPI.cancelFamilyRequest(id);
      navigate('/', { state: { message: data.message } });
    } catch (err) {
      setCancelError(err.response?.data?.error || 'Unable to cancel request');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!request) {
    return (
      <div className="space-y-4">
        <Link to="/" className="text-primary-600 text-sm">← Back to My Coverage</Link>
        <p className="text-slate-500">Request not found.</p>
      </div>
    );
  }

  const decisionLabel = {
    approved: 'Approved',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
    superseded: 'Superseded',
    pending: 'Awaiting decision',
  }[request.status] || request.status;

  return (
    <div className="space-y-6 max-w-3xl">
      <Link to="/" className="text-primary-600 text-sm">← Back to My Coverage</Link>
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileCheck className="w-7 h-7 text-primary-600" />
          {request.request_number}
        </h1>
        <p className="text-slate-500 mt-1 capitalize">{request.request_type} request — {request.status}</p>
      </div>

      <div className="card">
        <h2 className="font-semibold mb-4">Status</h2>
        <div className="flex items-center justify-between gap-2">
          {STATUS_STEPS.map((step, idx) => {
            const state = stepState(request, step.key);
            return (
              <div key={step.key} className="flex-1 flex flex-col items-center text-center">
                <StepIcon state={state} />
                <span className={`text-xs mt-1 ${state === 'current' ? 'font-semibold text-amber-700' : 'text-slate-500'}`}>
                  {step.key === 'decision' && state !== 'upcoming' ? decisionLabel : step.label}
                </span>
                {idx < STATUS_STEPS.length - 1 && (
                  <div className="hidden sm:block absolute" />
                )}
              </div>
            );
          })}
        </div>
        {request.status === 'rejected' && (
          <div className="mt-4 p-3 bg-red-50 rounded-lg text-sm text-red-900">
            <p className="font-medium">{REJECTION_LABELS[request.rejection_reason_code] || request.rejection_reason_code}</p>
            <p className="mt-1">{request.rejection_remarks}</p>
            <Link to={`/my-family?resubmit=${request.id}`} className="inline-block mt-2 text-primary-700 font-medium underline">
              Resubmit with corrections
            </Link>
          </div>
        )}
      </div>

      <div className="card grid sm:grid-cols-2 gap-4 text-sm">
        <div><span className="text-slate-400">Submitted</span><p>{new Date(request.submitted_at).toLocaleString('en-IN')}</p></div>
        <div><span className="text-slate-400">Type</span><p className="capitalize">{request.request_type}</p></div>
        <div><span className="text-slate-400">Current Premium</span><p>{formatCurrency(request.current_premium)}</p></div>
        <div><span className="text-slate-400">Proposed Premium</span><p>{formatCurrency(request.proposed_premium)}</p></div>
        <div><span className="text-slate-400">Difference</span><p className={request.premium_difference < 0 ? 'text-green-700' : 'text-red-700'}>
          {request.premium_difference > 0 ? '+' : ''}{formatCurrency(request.premium_difference)}
        </p></div>
        {request.reviewed_at && (
          <div><span className="text-slate-400">Reviewed</span><p>{new Date(request.reviewed_at).toLocaleString('en-IN')}</p></div>
        )}
      </div>

      {request.items?.length > 0 && (
        <div className="card">
          <h2 className="font-semibold mb-3">Requested Changes</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-500 border-b text-left">
                <th className="pb-2">Action</th><th className="pb-2">Member</th><th className="pb-2">Relation</th>
              </tr>
            </thead>
            <tbody>
              {request.items.map((item) => (
                <tr key={item.id} className="border-b border-slate-100">
                  <td className="py-2 capitalize">{item.action}</td>
                  <td className="font-medium">{item.member_name}</td>
                  <td className="capitalize">{item.relation?.replace(/_/g, ' ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {request.history?.length > 0 && (
        <div className="card">
          <h2 className="font-semibold mb-3">Timeline</h2>
          <ol className="space-y-3 text-sm">
            {request.history.map((h) => (
              <li key={h.id} className="flex gap-3">
                <span className="text-slate-400 shrink-0 w-36">{new Date(h.created_at).toLocaleString('en-IN')}</span>
                <span className="capitalize font-medium">{h.action?.replace(/_/g, ' ')}</span>
                {h.remarks && <span className="text-slate-500">— {h.remarks}</span>}
              </li>
            ))}
          </ol>
        </div>
      )}

      {request.status === 'pending' && (
        <div className="card border-amber-100">
          <button
            type="button"
            onClick={handleCancel}
            disabled={cancelling}
            className="btn-secondary flex items-center gap-2 text-red-700 border-red-200 hover:bg-red-50"
          >
            <Ban className="w-4 h-4" />
            {cancelling ? 'Cancelling…' : 'Cancel Pending Request'}
          </button>
          {cancelError && <p className="text-red-600 text-sm mt-2">{cancelError}</p>}
        </div>
      )}
    </div>
  );
}
